import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "../utils/api";
import { abrirArchivoSeguro } from "../utils/upload";
import { BadgeFormaPago } from "../utils/estados";
import {
  BadgeFase,
  DesglosePago,
  METODOS_PAGO,
  TablaItemsPago,
  DocumentosRespaldo,
  folioPago,
  montoTextoPago,
  netoTextoPago,
  numItem,
  textoArticulos,
} from "../utils/pagosUi";
import { EncabezadoDetalle, tarjetaTema, tituloSeccionTema } from "../components/TemaDetalle";
import Cargando from "../components/Cargando";

// Pantalla 2 de 3 — Gestionar pago: Contabilidad decide si puede pagar con la
// forma que propuso Compras o si la cambia (con motivo). Todavía no se ejecuta
// el pago: al confirmar, pasa a "En proceso" y sigue en Registrar pago.
function GestionarPago() {
  const { id } = useParams();
  const navigate = useNavigate();
  const usuario = JSON.parse(localStorage.getItem("usuario") || "null");
  const puedePagar = usuario?.rol === "pagos" || usuario?.rol === "administrador";

  const [pago, setPago] = useState(null);
  const [error, setError] = useState("");
  const [cambiando, setCambiando] = useState(false);
  const [nuevoMetodo, setNuevoMetodo] = useState("");
  const [motivo, setMotivo] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [verProductos, setVerProductos] = useState(null);

  useEffect(() => {
    apiFetch(`/api/pagos/${id}`)
      .then(setPago)
      .catch((err) => setError(err.message));
  }, [id]);

  if (error && !pago) return <p style={{ color: "#a3402f" }}>{error}</p>;
  if (!pago) return <Cargando />;
  // Un pago ya hecho no se gestiona: se ve en la pantalla de registro.
  if (pago.fase === "procesado") return <Navigate to={`/pagos/${pago.id}/registrar`} replace />;

  const propuesto = pago.metodo_propuesto || pago.metodo;
  const items = pago.items || [];
  const productosAbiertos = verProductos === null ? items.length <= 5 : verProductos;

  const gestionar = async (metodo, motivoCambio) => {
    setGuardando(true);
    setError("");
    try {
      await apiFetch(`/api/pagos/${pago.id}/gestionar`, {
        method: "POST",
        body: JSON.stringify({ metodo, motivo: motivoCambio }),
      });
      navigate(`/pagos/${pago.id}/registrar`);
    } catch (err) {
      setError(err.message);
      setGuardando(false);
    }
  };

  // Compras no definió la forma de pago: Pagos la elige ahora (sin motivo). El crédito no se elige aquí: lo marca
  // Compras en la cotización antes de aprobar.
  const definirMetodo = async () => {
    if (!nuevoMetodo) return setError("Elige la forma de pago.");
    setGuardando(true);
    setError("");
    try {
      await apiFetch(`/api/pagos/${pago.id}/gestionar`, {
        method: "POST",
        body: JSON.stringify({ metodo: nuevoMetodo }),
      });
      navigate(`/pagos/${pago.id}/registrar`);
    } catch (err) {
      setError(err.message);
      setGuardando(false);
    }
  };

  const abrirCambio = () => {
    const primeroDistinto = METODOS_PAGO.find((m) => m.valor !== pago.metodo);
    setNuevoMetodo(primeroDistinto ? primeroDistinto.valor : "");
    setMotivo("");
    setError("");
    setCambiando(true);
  };

  const confirmarCambio = () => {
    if (!nuevoMetodo) return setError("Elige la nueva forma de pago.");
    if (!motivo.trim()) return setError("Escribe el motivo del cambio.");
    gestionar(nuevoMetodo, motivo.trim());
  };

  return (
    <div style={{ maxWidth: 860, margin: "0 auto" }}>
      <Link to="/pagos" style={styles.volver}>
        ← Regresar a Pagos
      </Link>
      <EncabezadoDetalle
        titulo="💳 Gestionar pago"
        enlace={`/solicitud/${pago.solicitud_id}`}
        enlaceTexto={`Solicitud ${folioPago(pago.solicitud_id)}`}
        insignia={<BadgeFase fase={pago.fase} />}
        subtitulo={pago.descripcion_requerimiento}
        etiquetaMonto={netoTextoPago(pago) ? "Neto a pagar" : "Monto"}
        monto={netoTextoPago(pago) || montoTextoPago(pago)}
        notaMonto={netoTextoPago(pago) ? `Factura: ${montoTextoPago(pago)}` : null}
        tono={pago.fase === "en_proceso" ? "proceso" : "pendiente"}
      />

      <div style={styles.card}>
        <div style={styles.datos}>
          <Dato etiqueta="Proveedor" valor={pago.proveedor_nombre} />
          <Dato etiqueta="Artículos" valor={textoArticulos(items)} />
          <Dato etiqueta="Solicitante" valor={`${pago.solicitante_nombre} · ${pago.area_nombre}`} />
          <Dato etiqueta="Aprobado por" valor={pago.aprobado_por_nombre || "—"} />
          <Dato
            etiqueta="Cotización"
            valor={
              pago.archivo_cotizacion ? (
                <a href="#" onClick={(e) => abrirArchivoSeguro(e, pago.archivo_cotizacion)} style={styles.link}>
                  📄 Ver cotización
                </a>
              ) : (
                "—"
              )
            }
          />
        </div>
      </div>

      {pago.metodo_por_definir && (
        <div style={styles.card}>
          <div style={styles.tituloSeccion}>Forma de pago por definir</div>
          {puedePagar ? (
            <>
              <p style={styles.pregunta}>
                Compras no definió cómo se va a pagar esta compra. ¿Con qué forma se paga?
              </p>
              <div style={styles.opciones}>
                {METODOS_PAGO.map((m) => (
                  <label
                    key={m.valor}
                    style={{ ...styles.opcion, ...(nuevoMetodo === m.valor ? styles.opcionActiva : {}) }}
                  >
                    <input
                      type="radio"
                      name="metodo-definir"
                      value={m.valor}
                      checked={nuevoMetodo === m.valor}
                      onChange={() => setNuevoMetodo(m.valor)}
                    />
                    {m.icono} {m.texto}
                  </label>
                ))}
              </div>
              {error && <p style={{ color: "#a3402f", fontSize: "0.85rem" }}>{error}</p>}
              <div style={styles.acciones}>
                <button type="button" disabled={guardando || !nuevoMetodo} onClick={definirMetodo} style={styles.botonPrincipal}>
                  {guardando ? "Guardando..." : "✓ Confirmar forma de pago"}
                </button>
              </div>
            </>
          ) : (
            <p style={styles.soloLectura}>Compras no la definió todavía; quien paga la define al gestionar el pago.</p>
          )}
        </div>
      )}

      {!pago.metodo_por_definir && (
      <div style={styles.card}>
        <div style={styles.tituloSeccion}>Forma de pago propuesta</div>
        <div style={styles.propuesta}>
          <BadgeFormaPago valor={propuesto} />
          <span style={styles.subtituloChico}>Propuesta por: Compras</span>
        </div>

        {pago.cambio_de_medio && (
          <div style={styles.avisoCambio}>
            Ya se cambió a <BadgeFormaPago valor={pago.metodo} /> por {pago.cambiado_por_nombre || "Contabilidad"}
            {pago.motivo_cambio ? ` — motivo: ${pago.motivo_cambio}` : ""}.
          </div>
        )}

        {!puedePagar ? (
          <p style={styles.soloLectura}>Solo quien paga puede gestionar este pago.</p>
        ) : pago.fase === "en_proceso" && !cambiando ? (
          <div style={styles.enProceso}>
            <p style={{ margin: "0 0 0.6rem", color: "#1d5fa8", fontWeight: 600 }}>
              🔄 Este pago ya está en proceso con <BadgeFormaPago valor={pago.metodo} />. Falta registrar el pago.
            </p>
            <div style={styles.acciones}>
              <button type="button" onClick={() => navigate(`/pagos/${pago.id}/registrar`)} style={styles.botonPrincipal}>
                Continuar al registro del pago →
              </button>
              <button type="button" onClick={abrirCambio} style={styles.botonSecundario}>
                🔄 Cambiar forma de pago
              </button>
            </div>
          </div>
        ) : !cambiando ? (
          <>
            <p style={styles.pregunta}>
              ¿Pueden realizar el pago con <strong>{pago.metodo}</strong>?
            </p>
            <div style={styles.acciones}>
              <button
                type="button"
                disabled={guardando}
                onClick={() => gestionar(pago.metodo, "")}
                style={styles.botonPrincipal}
              >
                {guardando ? "Guardando..." : "✓ Utilizar este medio"}
              </button>
              <button type="button" disabled={guardando} onClick={abrirCambio} style={styles.botonSecundario}>
                🔄 Cambiar forma de pago
              </button>
            </div>
          </>
        ) : (
          <div style={styles.panelCambio}>
            <div style={styles.tituloSeccion}>Cambiar forma de pago</div>
            <div style={styles.opciones}>
              {METODOS_PAGO.map((m) => {
                const esActual = m.valor === pago.metodo;
                return (
                  <label
                    key={m.valor}
                    style={{
                      ...styles.opcion,
                      ...(nuevoMetodo === m.valor ? styles.opcionActiva : {}),
                      ...(esActual ? { opacity: 0.5, cursor: "not-allowed" } : {}),
                    }}
                  >
                    <input
                      type="radio"
                      name="metodo"
                      value={m.valor}
                      disabled={esActual}
                      checked={nuevoMetodo === m.valor}
                      onChange={() => setNuevoMetodo(m.valor)}
                    />
                    {m.icono} {m.texto}
                    {esActual && <span style={styles.etiquetaActual}>actual</span>}
                  </label>
                );
              })}
            </div>
            <label style={styles.etiqueta}>
              Motivo del cambio
              <textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                rows={2}
                placeholder="Ej. No hay efectivo disponible para este pago."
                style={styles.textarea}
              />
            </label>
            <div style={styles.acciones}>
              <button type="button" disabled={guardando} onClick={() => setCambiando(false)} style={styles.botonSecundario}>
                Cancelar
              </button>
              <button type="button" disabled={guardando} onClick={confirmarCambio} style={styles.botonPrincipal}>
                {guardando ? "Guardando..." : "Confirmar cambio"}
              </button>
            </div>
          </div>
        )}

        {error && <p style={{ color: "#a3402f", fontSize: "0.85rem", marginBottom: 0 }}>{error}</p>}
      </div>
      )}

      <DocumentosRespaldo datos={pago} />

      <div style={styles.card}>
        <button type="button" onClick={() => setVerProductos(!productosAbiertos)} style={styles.toggleProductos}>
          <span style={styles.tituloSeccion}>Productos incluidos</span>
          <span style={styles.subtituloChico}>
            {textoArticulos(items)} · {productosAbiertos ? "Ocultar ▴" : "Ver detalle ▾"}
          </span>
        </button>
        {productosAbiertos && (
          <div style={{ marginTop: "0.7rem" }}>
            <TablaItemsPago items={items} moneda={pago.moneda} />
            <DesglosePago pago={pago} />
          </div>
        )}
        {(pago.pendientes_solicitud || []).length > 0 && (
          <p style={styles.notaPendientes}>
            De esta misma solicitud todavía no se aprobó:{" "}
            {pago.pendientes_solicitud.map((x) => `ítem ${numItem(x.numero_item)}`).join(", ")}.
          </p>
        )}
      </div>
    </div>
  );
}

function Dato({ etiqueta, valor }) {
  return (
    <div>
      <div style={styles.etiquetaDato}>{etiqueta}</div>
      <div style={styles.valorDato}>{valor}</div>
    </div>
  );
}

const styles = {
  volver: { color: "var(--tema-titulo, #0b3c60)", fontWeight: 600, fontSize: "0.88rem", textDecoration: "none" },
  card: {
    border: "1px solid #e2e6ea",
    borderRadius: 14,
    padding: "1.1rem 1.3rem",
    marginTop: "1rem",
    background: "#fff",
    boxShadow: "0 1px 3px rgba(27,42,74,0.06)",
    ...tarjetaTema,
  },
  datos: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: "0.9rem 1.5rem",
  },
  etiquetaDato: { fontSize: "0.68rem", fontWeight: 700, color: "#8993a1", textTransform: "uppercase", letterSpacing: "0.02em", marginBottom: "0.15rem" },
  valorDato: { fontSize: "0.9rem", color: "var(--tema-titulo, #0b3c60)", fontWeight: 600 },
  link: { color: "var(--tema-primario, #2f6690)", textDecoration: "none", fontWeight: 600 },
  tituloSeccion: tituloSeccionTema,
  subtituloChico: { fontSize: "0.78rem", color: "#8993a1" },
  propuesta: { display: "flex", alignItems: "center", gap: "0.7rem", marginTop: "0.5rem" },
  avisoCambio: { marginTop: "0.7rem", padding: "0.5rem 0.75rem", background: "#fdf3e3", color: "#7a4a17", borderRadius: 8, fontSize: "0.84rem" },
  pregunta: { margin: "1.1rem 0 0.6rem", fontSize: "0.95rem", color: "var(--tema-titulo, #0b3c60)" },
  acciones: { display: "flex", gap: "0.6rem", flexWrap: "wrap", marginTop: "0.8rem" },
  enProceso: { marginTop: "1rem", padding: "0.8rem 1rem", background: "var(--tema-suave, #eef4fb)", borderRadius: 10 },
  soloLectura: { margin: "0.9rem 0 0", fontSize: "0.85rem", color: "#5b6472" },
  botonPrincipal: {
    background: "#0f7c8a",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "0.6rem 1.1rem",
    fontSize: "0.88rem",
    fontWeight: 700,
    cursor: "pointer",
  },
  botonSecundario: {
    background: "#fff",
    color: "var(--tema-titulo, #0b3c60)",
    border: "1px solid #d3d9df",
    borderRadius: 8,
    padding: "0.6rem 1.1rem",
    fontSize: "0.88rem",
    fontWeight: 600,
    cursor: "pointer",
  },
  panelCambio: { marginTop: "1rem", padding: "1rem", border: "1px solid #e2e6ea", borderRadius: 10, background: "#fafbfc" },
  opciones: { display: "flex", flexWrap: "wrap", gap: "0.5rem", margin: "0.7rem 0 0.9rem" },
  opcion: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.4rem",
    border: "1px solid #d3d9df",
    borderRadius: 8,
    padding: "0.45rem 0.8rem",
    fontSize: "0.86rem",
    color: "var(--tema-titulo, #0b3c60)",
    background: "#fff",
    cursor: "pointer",
  },
  opcionActiva: { borderColor: "#0f7c8a", background: "#e6f4f6", fontWeight: 700 },
  etiquetaActual: { fontSize: "0.68rem", color: "#8993a1", fontWeight: 600 },
  etiqueta: { display: "flex", flexDirection: "column", gap: "0.3rem", fontSize: "0.82rem", fontWeight: 600, color: "var(--tema-titulo, #0b3c60)" },
  textarea: { padding: "0.5rem 0.6rem", border: "1px solid #d3d9df", borderRadius: 6, fontFamily: "inherit", fontSize: "0.88rem", resize: "vertical" },
  toggleProductos: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  notaPendientes: { margin: "0.8rem 0 0", fontSize: "0.78rem", color: "#8993a1" },
};

export default GestionarPago;
